import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';

import { tokenManager } from '@/services/tokenManager';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';
import { useNotificationsStore } from '@/stores/notifications';

import { generateRouteRecords, routeDefinitions } from './route-config';

const generatedRoutes = generateRouteRecords(routeDefinitions);

const routes: RouteRecordRaw[] = [
  {
    path: '/admin',
    redirect: '/admin/dashboard',
  },
  {
    path: '/company/:companyId',
    redirect: to => `/company/${to.params.companyId}/dashboard`,
  },
  {
    path: '/',
    name: 'root',
    component: () => import('../views/Customer/Login.vue'),
    meta: { hideNavbar: true },
  },
  ...generatedRoutes,
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async to => {
  const authStore = useAuthStore();
  const companyStore = useCompanyStore();
  const notifications = useNotificationsStore();

  const context = to.meta.context as 'employee' | 'customer' | undefined;

  if (context) {
    authStore.setContext(context);
  }

  if (to.name === 'root') {
    if (!authStore.employeeUser && !authStore.customerUser) {
      await authStore.initAuth();
    }

    if (authStore.isCustomer) {
      const companyId = await companyStore.autoSelectCompany();
      if (companyId) {
        return { name: 'customer.dashboard', params: { companyId } };
      }
      notifications.warning(
        'You are not associated with any companies yet. Please contact your administrator for access.',
        'No Companies Available',
      );
      await authStore.logoutCustomer();
      return { name: 'customer.login' };
    }
  }

  if (to.meta.requiresGuest) {
    const isAuthenticated =
      context === 'employee' ? tokenManager.isEmployeeAuthenticated() : tokenManager.isCustomerAuthenticated();

    if (isAuthenticated) {
      if (context === 'employee') {
        return { name: 'admin.dashboard' };
      }
      const companyId = await companyStore.autoSelectCompany();
      if (companyId) {
        return { name: 'customer.dashboard', params: { companyId } };
      }
      return { name: 'customer.login' };
    }
  }

  if (to.meta.requiresAuth) {
    if (!authStore.employeeUser && !authStore.customerUser) {
      await authStore.initAuth();
    }

    if (to.meta.requiresEmployee && !authStore.isEmployee) {
      return { name: 'admin.login' };
    }

    if (to.meta.requiresCustomer && !authStore.isCustomer) {
      return { name: 'customer.login' };
    }

    if (!authStore.isAuthenticated) {
      if (context === 'employee') {
        return { name: 'admin.login' };
      }
      return { name: 'customer.login' };
    }

    if (
      authStore.currentUser?.force_password_change &&
      to.name !== 'security.password' &&
      to.name !== 'admin.security.password'
    ) {
      if (context === 'employee') {
        return { name: 'admin.security.password' };
      }
      return { name: 'security.password' };
    }

    if (to.meta.requiresCompany) {
      const companyId = to.params.companyId as string;

      if (!companyId) {
        const autoSelectedId = await companyStore.autoSelectCompany();
        if (autoSelectedId) {
          return { name: 'customer.dashboard', params: { companyId: autoSelectedId } };
        }
        return { name: 'customer.login' };
      }

      if (!companyStore.hasSelectedCompany || companyStore.selectedCompanyId !== companyId) {
        await companyStore.initialize(true);
        await companyStore.selectCompany(companyId);
      }

      if (to.meta.requiresOwner && !companyStore.isOwner) {
        return { name: 'customer.dashboard', params: { companyId } };
      }
    }

    if (to.meta.permission) {
      const user = authStore.currentUser;

      const hasPermission = user?.permissions?.includes(to.meta.permission) ?? false;

      if (!hasPermission) {
        console.warn(`Access denied to route: ${String(to.name)} - Missing permission: ${to.meta.permission}`);

        if (context === 'employee') {
          return { name: 'admin.dashboard' };
        } else if (context === 'customer') {
          const companyId = to.params.companyId as string;
          return { name: 'customer.dashboard', params: { companyId } };
        }
      }
    }
  }
});

export default router;

export { Routes, childRoute } from './routes';

export { generateMenuItems, type MenuItem } from './route-config';

export type {
  RouteName,
  RouteParams,
  RoutesWithParams,
  RoutesWithoutParams,
  TypedRouteLocation,
  RouteConstant,
} from './routes';
